import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Card, CardContent, Paper } from '@mui/material';
import PowerBiEmbedded from '../../components/PowerBIPublicEmbed/PowerBiEmbedded';
import ExpandableSection from '../../components/ExpandableSection/ExpandableSection';
import { powerbiConfig } from '../../config/powerbi';
import QuickViewCard from './components/QuickViewCard/QuickViewCard';
import OwlGreeting from './components/OwlGreeting';
import { useGetApiApprovalQueueQuery } from '../../app/athenaApiGen';
import { getEnabledMenuItems } from '../utilities';

const SECTION_HEIGHTS = {
  masterSchedule: '360px',
  approvalQueue: '520px',
  financials: '650px',
};

const Home = () => {
  // Gate the Financials dashboard section on the same `view-financial-views`
  // action that hides the Financials nav item and /financials route
  // (see getEnabledMenuItems → enabledItems.financials). ATH-1570.
  const { actions } = useSelector((state) => state.user);
  const financialsEnabled = !!getEnabledMenuItems(actions).financials;

  // Controlled open/close state for each accordion (the maximize-to-full-view
  // state now lives inside each ExpandableSection). Used to show the owl greeting
  // only when all three are collapsed. Approval Queue starts collapsed
  // and auto-expands once the pending-count fetch resolves (see effect
  // below) — that way a user with an empty queue isn't presented with
  // a big empty section on landing.
  const [accordionOpen, setAccordionOpen] = useState({
    approvalQueue: false,
    masterSchedule: true,
    financials: true,
  });

  // Cheap (limit: 1) probe just for the pending totalCount — same shape
  // ApprovalQueue.tsx uses for its tab badge counts, so the response is
  // already in RTK Query cache when the user lands on that page next.
  const { data: pendingApprovalsData } = useGetApiApprovalQueueQuery({
    page: 1,
    limit: 1,
    approvalState: 'pending',
  });

  // Apply the auto-expand exactly once on first data resolution. After
  // that the user's own toggling owns the open state — re-applying on
  // every refetch would yank the panel back open after they collapsed it.
  const initialExpandAppliedRef = useRef(false);
  useEffect(() => {
    if (initialExpandAppliedRef.current) return;
    if (pendingApprovalsData?.totalCount === undefined) return;
    initialExpandAppliedRef.current = true;
    if (pendingApprovalsData.totalCount > 0) {
      setAccordionOpen((prev) => ({ ...prev, approvalQueue: true }));
    }
  }, [pendingApprovalsData?.totalCount]);

  const handleAccordionChange = (key) => (_event, isExpanded) => {
    setAccordionOpen((prev) => ({ ...prev, [key]: isExpanded }));
  };

  const allAccordionsCollapsed =
    !accordionOpen.approvalQueue &&
    !accordionOpen.masterSchedule &&
    // When the user can't see Financials, don't let its (hidden) accordion
    // state keep the owl greeting from showing.
    (!financialsEnabled || !accordionOpen.financials);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        px: 3,
        pt: 1,
        pb: 3,
        overflow: 'auto',
      }}
    >
        {/* Section 1: Approval Queue */}
        <ExpandableSection
          title="Approvals"
          expanded={accordionOpen.approvalQueue}
          onExpandedChange={handleAccordionChange('approvalQueue')}
          collapsedHeight={SECTION_HEIGHTS.approvalQueue}
        >
          {(isFullscreen) => (
            <QuickViewCard
              type="Approval Queue"
              hideHeader
              fixedPageSize={isFullscreen ? 25 : 5}
            />
          )}
        </ExpandableSection>

        {/* Section 2: Master Schedule */}
        <ExpandableSection
          title="Schedule"
          expanded={accordionOpen.masterSchedule}
          onExpandedChange={handleAccordionChange('masterSchedule')}
          collapsedHeight={SECTION_HEIGHTS.masterSchedule}
        >
          <QuickViewCard type="Master Schedule" hideHeader />
        </ExpandableSection>

        {/* Section 3: Financials - Power BI Dashboard.
            Hidden unless the user has the `view-financial-views` action
            (ATH-1570) — mirrors the nav + /financials route gating. */}
        {financialsEnabled && (
          <ExpandableSection
            title="Financials"
            expanded={accordionOpen.financials}
            onExpandedChange={handleAccordionChange('financials')}
            collapsedHeight={SECTION_HEIGHTS.financials}
          >
            <Card
              sx={{
                pt: 2,
                px: 2,
                pb: 2,
                height: '100%',
              }}
            >
              <CardContent sx={{ width: '100%', height: '100%', overflow: 'auto', p: 0 }}>
                <Paper
                  sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    position: 'relative',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'white',
                    pt: 1,
                    px: 2,
                    pb: 2,
                  }}
                >
                  <PowerBiEmbedded
                    reportId={powerbiConfig.home.reportId}
                    datasetId={powerbiConfig.home.datasetId}
                    fitToPage
                  />
                </Paper>
              </CardContent>
            </Card>
          </ExpandableSection>
        )}
      {allAccordionsCollapsed && <OwlGreeting />}
    </Box>
  );
};

export default Home;
